import type { MembershipResponse, MembershipRole } from '../types'

const ROLE_ORDINAL: Record<MembershipRole, number> = {
  VIEWER: 0,
  CONTRIBUTOR: 1,
  MANAGER: 2,
  ADMIN: 3,
  OWNER: 4,
}

/**
 * Work-track roles carry access to goal content, dashboards, and capacity.
 * The admin track (ADMIN/OWNER) configures the workspace but does not own
 * work content — mirroring backend authorization.
 */
export function isWorkRole(role: MembershipRole): boolean {
  return role === 'VIEWER' || role === 'CONTRIBUTOR' || role === 'MANAGER'
}

/**
 * Direct-entity role check, ordinal-only -- no work/admin-track filter, no
 * ancestor scopes. Mirrors backend `User.hasRoleAtLeast`, which the admin
 * track (ADMIN/OWNER) satisfies too, unlike [hasMembershipAtOrAbove].
 */
export function hasDirectRoleAtOrAbove(
  memberships: MembershipResponse[],
  minRole: MembershipRole,
  entityType: string,
  entityId?: string | null,
): boolean {
  if (!entityId) return false
  const threshold = ROLE_ORDINAL[minRole]

  return memberships.some((membership) =>
    membership.status === 'ACTIVE'
    && membership.entityType === entityType
    && membership.entityId === entityId
    && ROLE_ORDINAL[membership.role] >= threshold,
  )
}

export function hasMembershipAtOrAbove(
  memberships: MembershipResponse[],
  minRole: MembershipRole,
  scopes: Array<{ entityType: string; entityId?: string | null }>,
): boolean {
  const threshold = ROLE_ORDINAL[minRole]

  return memberships.some((membership) =>
    membership.status === 'ACTIVE'
    && isWorkRole(membership.role)
    && ROLE_ORDINAL[membership.role] >= threshold
    && scopes.some(
      (scope) =>
        !!scope.entityId
        && scope.entityType === membership.entityType
        && scope.entityId === membership.entityId,
    ),
  )
}
